// src/pages/organization/event/ShowAllTeamToCreateEvent.js

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMainPageTitle } from "../../../contexts/PageContext";
import { getGenderDisplay } from "../../../components/organization/team/GetGenderDisplay";
import ProfileImage from "../../../components/profileImage";



const AllTeam = () => {
  const navigate = useNavigate();
  const { updatePageTitle } = useMainPageTitle();
  useEffect(() => {
    updatePageTitle("Create Event", "Select Team to Create Event");
  }, [updatePageTitle]);

  const [page, setPage] = useState(1);
  const [limit] = useState(30);
  const [teamsData, setTeamsData] = useState({
    teams: [],
    pagination: { total: 0, page: 1, pages: 1, limit: 8 },
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAllTeams = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1/team/get-all-my-organization-teams?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch teams");
      const data = await response.json();

      if (data.message === "Teams fetched successfully") {
        setTeamsData({
          teams: data.teams || [],
          pagination: data.pagination || { total: 0, page: 1, pages: 1, limit },
        });
      } else {
        throw new Error(data.message || "Failed to fetch teams");
      }
    } catch (err) {
      setError(err.message);
      setTeamsData({
        teams: [],
        pagination: { total: 0, page: 1, pages: 1, limit },
      });
    } finally {
      setIsLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    fetchAllTeams();
  }, [fetchAllTeams]);

  const TeamCard = ({ team }) => {
    // Calculate number of players - ensure members is an array
    const playerCount = Array.isArray(team.members) 
      ? team.members.filter((member) => member.type === "player").length 
      : 0;


    const handleTeamClick = (teamId) => {
      navigate(`/organization/events/create/team/${teamId}`);
    };

    return (
      <div
        className="bg-white/50 backdrop-blur-sm rounded-xl p-6 hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer"
        onClick={() => handleTeamClick(team._id)}
      >
        <div className="relative mb-4 aspect-square rounded-lg overflow-hidden bg-gray-100">
          <ProfileImage
            image={team.logo}
            alt={team.name}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="space-y-2">
          <h3 className="font-semibold text-lg text-gray-800 truncate">
            {team.name}
          </h3> 
          {team.pendingRequestsCount > 0 && (
            <div className="flex items-center space-x-2">
              <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                {team.pendingRequestsCount} pending requests
              </span>
            </div>
          )}
          <p className="text-sm text-gray-500 truncate">@{team.uniqueName}</p>
          <div className="flex items-center space-x-2">
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
              {team.sport}
            </span>
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
              {team.type}
            </span>
          </div>
          <div className="flex items-center space-x-2 text-gray-500 text-sm">
            <span className="capitalize">{getGenderDisplay(team.gender)}</span>
            <span>•</span>
            <div className="flex items-center space-x-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
              </svg>
              <span>{playerCount} players</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-8">
      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F15213]"></div>
        </div>
      )}

      {/* Error State */}
      {!isLoading && error && (
        <div className="flex justify-center items-center h-64 text-red-500">
          Error: {error}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && teamsData.teams.length === 0 && (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <svg
            className="w-16 h-16 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
          <p className="text-xl font-medium">No teams found</p>
          <p className="text-sm mt-2">Try adjusting your search or filters</p>
        </div>
      )}

      {/* Teams Grid */}
      {!isLoading && !error && teamsData.teams.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {teamsData.teams.map((team) => (
              <TeamCard key={team._id} team={team} />
            ))}
          </div>

          {/* Pagination */}
          {teamsData.pagination.pages > 1 && (
            <div className="flex justify-center mt-8 space-x-2">
              {[...Array(teamsData.pagination.pages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i + 1)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    page === i + 1
                      ? "bg-[#F15213] text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AllTeam;
